/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Text from '@Text';
import { loadGoogleMapsScript } from './googleMaps.helpers';
import { resolveLanguage } from './copy/clientApiCopy';
import useClientApiCopy from './copy/useClientApiCopy';
import makeSolarMapPinStyles from './makeSolarMapPinStyles';

const PROPERTY_ZOOM = 19;

const formatCoords = ({ latitude, longitude }) =>
  `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;

// A teardrop pin in the theme colour with a white "Drag to move" label centred
// below it, as a data-URI SVG marker icon (matching the demo). Baking the label
// into the icon keeps it with the pin as it is dragged. The pin tip sits at
// (70, 38), so the icon is anchored there.
const PIN_SVG_WIDTH = 140;
const PIN_SVG_HEIGHT = 66;
const PIN_TIP = { x: 70, y: 38 };

const buildPinSvgUrl = (colour, label) => {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${PIN_SVG_WIDTH}" height="${PIN_SVG_HEIGHT}" viewBox="0 0 ${PIN_SVG_WIDTH} ${PIN_SVG_HEIGHT}">` +
    `<defs><filter id="s" x="-20%" y="-20%" width="140%" height="160%">` +
    `<feDropShadow dx="0" dy="1" stdDeviation="1.5" flood-opacity="0.25"/></filter></defs>` +
    // Teardrop: head centred on x=70, tip at (70, 38). White border, green fill.
    `<path d="M70 4C62.8 4 57 9.8 57 17c0 9.75 13 21 13 21s13-11.25 13-21C83 9.8 77.2 4 70 4z" fill="${colour}" stroke="#ffffff" stroke-width="3" filter="url(#s)"/>` +
    `<circle cx="70" cy="17" r="4.5" fill="#ffffff"/>` +
    // Label box, centred below the pin.
    `<rect x="36" y="44" width="68" height="18" rx="4" fill="#ffffff" filter="url(#s)"/>` +
    `<text x="70" y="56" text-anchor="middle" font-family="Arial, sans-serif" font-size="10" font-weight="600" fill="#4f4f4f">${label}</text>` +
    `</svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

/**
 * The satellite map and draggable pin. Kept as its own component so the map is
 * created in a mount effect that runs after MUI's portal has attached the
 * container ref: an effect in the parent could see a null ref.
 */
const PinMap = ({
  start,
  localeCode,
  onMove,
  classes,
  mapUnavailable,
  markerColour,
  markerTitle,
}) => {
  const mapContainerRef = useRef(null);
  const markerRef = useRef(null);
  const [isUnavailable, setIsUnavailable] = useState(false);

  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;

  useEffect(() => {
    if (!apiKey) {
      setIsUnavailable(true);
      return undefined;
    }

    let isActive = true;
    const center = { lat: start.latitude, lng: start.longitude };

    loadGoogleMapsScript(apiKey, resolveLanguage(localeCode))
      .then(() => {
        if (!isActive || !mapContainerRef.current) {
          return;
        }

        const { google } = window;
        const map = new google.maps.Map(mapContainerRef.current, {
          center,
          zoom: PROPERTY_ZOOM,
          mapTypeId: 'satellite',
          tilt: 0,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: 'greedy',
        });

        const marker = new google.maps.Marker({
          position: center,
          map,
          draggable: true,
          title: markerTitle,
          icon: {
            url: buildPinSvgUrl(markerColour, markerTitle),
            size: new google.maps.Size(PIN_SVG_WIDTH, PIN_SVG_HEIGHT),
            anchor: new google.maps.Point(PIN_TIP.x, PIN_TIP.y),
          },
        });
        markerRef.current = marker;

        const moveTo = (event) => {
          const latitude = event.latLng.lat();
          const longitude = event.latLng.lng();
          marker.setPosition({ lat: latitude, lng: longitude });
          onMove({ latitude, longitude });
        };

        marker.addListener('dragend', moveTo);
        map.addListener('click', moveTo);
      })
      .catch(() => {
        if (isActive) {
          setIsUnavailable(true);
        }
      });

    return () => {
      isActive = false;
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isUnavailable) {
    return (
      <Box css={classes.unavailable} data-testid="solarPinMapUnavailable">
        <Text variant="text">{mapUnavailable}</Text>
      </Box>
    );
  }

  return (
    <Box
      ref={mapContainerRef}
      css={classes.mapContainer}
      data-testid="solarPinMapContainer"
    />
  );
};

/**
 * "Not your property?" modal: the user drags or clicks a pin onto the right
 * building, then confirms. Confirm stays disabled until the pin moves, and
 * `onConfirm` receives the pin's coordinates for the solar load override. The
 * selected location's postcode and address are not changed.
 */
const SolarMapPinModal = ({
  open,
  start,
  address,
  localeCode,
  onConfirm,
  onClose,
}) => {
  const theme = useTheme();
  const classes = makeSolarMapPinStyles({ theme });
  const copy = useClientApiCopy();
  const modalContainerRef = useRef(null);
  const markerColour =
    theme?.mvf?.palette?.component?.continueButtonColor ?? '#03624c';

  const [coords, setCoords] = useState(null);
  const hasMoved = coords !== null;
  const shownCoords = coords ?? start;

  // Each open starts from the pin's start position: a prior move must not carry
  // its "moved" state into the next open.
  useEffect(() => {
    if (!open) {
      setCoords(null);
    }
  }, [open]);

  const getParentElement = () => modalContainerRef.current?.parentElement;

  return (
    <div ref={modalContainerRef}>
      <Modal
        open={open}
        onClose={onClose}
        container={getParentElement}
        style={{ position: 'absolute' }}
        BackdropProps={{ style: { position: 'absolute' } }}
      >
        <Box css={classes.modal} data-testid="solarPinModal">
          <Box css={classes.header}>
            <h2 css={classes.title}>{copy.solar.pinModal.title}</h2>
            <button
              type="button"
              css={classes.closeButton}
              aria-label={copy.solar.pinModal.close}
              onClick={onClose}
              data-testid="solarPinClose"
            >
              ×
            </button>
          </Box>
          <p css={classes.instructions}>{copy.solar.pinModal.instructions}</p>
          <Box css={classes.currentAddress}>
            <span css={classes.addressLabel}>
              {copy.solar.pinModal.currentAddressLabel}
            </span>
            <span css={classes.addressValue}>{address}</span>
          </Box>
          {open && (
            <PinMap
              start={start}
              localeCode={localeCode}
              onMove={setCoords}
              classes={classes}
              mapUnavailable={copy.solar.mapUnavailable}
              markerColour={markerColour}
              markerTitle={copy.solar.pinModal.markerLabel}
            />
          )}
          <Box css={classes.coordinates}>
            <span css={classes.coordLabel}>
              {copy.solar.pinModal.coordinatesLabel}
            </span>
            <span css={classes.coordValue} data-testid="solarPinCoordinates">
              {formatCoords(shownCoords)}
            </span>
          </Box>
          <Box css={classes.actions}>
            <button
              type="button"
              css={classes.cancelButton}
              onClick={onClose}
              data-testid="solarPinCancel"
            >
              {copy.solar.pinModal.cancel}
            </button>
            <button
              type="button"
              css={classes.confirmButton}
              disabled={!hasMoved}
              onClick={() => onConfirm(coords)}
              data-testid="solarPinConfirm"
            >
              {copy.solar.pinModal.confirm}
            </button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default SolarMapPinModal;
